/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 컬러 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/color/Color.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 27.
 */
import * as parent from '@components/form/field/input/Input';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import './Color.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Color extends parent.Input {
    /**
     * Input 확장타입
     */
    inputVariant = 'color';

    /**
     * button DOM
     */
    $button: Dom = null;

    /**
     * 숫자 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Color> = null) {
        super(properties);
    }

    /**
     * INPUT 필드 DOM 을 가져온다.
     *
     * @returns INPUT 필드 DOM
     */
    $getInput(): Dom<HTMLInputElement> {
        if (this.$input === null) {
            this.$input = super.$getInput(false, 'text');

            this.$input.on('input', async (me) => {
                let value = me.getValue();
                if (value.length == 0) {
                    value = null;
                }
                await this.setValue(value, false, false);
                const validation = this.getValidationHistory(value, true);
                this.updateValidation(validation);
            });

            this.$input.on('blur', async (me) => {
                let value = me.getValue();
                if (value.length == 0) {
                    value = null;
                }
                await this.setValue(value, false, false);
                await this.isValid(value);
            });
        }

        return this.$input;
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button', {
                type: 'button',
                'data-action': 'picker',
            });

            this.$button.on('pointerdown', () => {
                // this.toggle();
            });
        }

        return this.$button;
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<number> {
        return this.value;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     */
    async setValue(value: string | null, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        value = value ?? '';
        if (value.length == 0) {
            value = null;
        } else {
            value =
                '#' +
                value
                    .replace(/^#/, '')
                    .replace(/[^a-fA-F0-9]/g, '')
                    .slice(0, 6)
                    .toUpperCase();
        }

        if (is_update == true) {
            if (value === null) {
                this.$getInput().setValue('');
            } else {
                this.$getInput().setValue(value);
            }
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 필드값 유효성결과
     */
    protected async validate(value: any = undefined): Promise<{ valid: boolean; message?: string }> {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        if (value === undefined) {
            value = await this.getValue();
        }

        let validation = this.getValidationHistory(value);
        if (validation === null) {
            validation = { valid: true };

            const isBlank = await this.isBlank(value);
            if (this.allowBlank === false && isBlank == true) {
                validation.valid = false;
                validation.message = await Language.getError('REQUIRED').getText();
            } else if (typeof this.validator == 'function') {
                const results = await this.validator(value, this);
                if (typeof results == 'boolean') {
                    validation.valid = results;
                } else {
                    validation.valid = false;
                    validation.message = results;
                }
            } else if ((value ?? this.defaultValue ?? '').search(/^#[a-zA-Z0-9]{6}$/) === -1) {
                validation.valid = false;
                validation.message = await Language.getError('HEXCOLOR_ONLY').getText();
            }
        }

        return validation;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        // this.collapse();
        this.$getButton().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        await super.renderField();
        this.$getContent().append(this.$getButton());
    }
}
